import { useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { InformationTip } from '@bloomays-lib/ui.shared';
import { StatusChip, RowActionsMenu, AvatarCell, avatarToneFromSeed } from '@bloomays-lib/ui.shared/datagrid';
import type { DataGridV2RowAction, DataGridV2Tone } from '@bloomays-lib/ui.shared/datagrid';
import { TemplateContract, type ResolvedContact } from '@bloomays-lib/types.shared';
import { dateFormat, initialsFromName, isContractSigned } from '@bloomays-lib/utils.shared';
import { resolveSignerDisplayName, type DocumentVolet } from '../../helpers/legalContracts';
import LegalContractFile from './LegalContractFile';
import ContractTypeBadge from './ContractTypeBadge';

export type DocumentVoletKind = TemplateContract.SST | TemplateContract.PDS;

export type DocumentVoletCardProps = {
  volet: DocumentVolet | null;
  voletKind: DocumentVoletKind;
  missionRecordId: string;
  /** Signer for this volet (not the bloomer); reused by the upload modal. */
  resolvedSigner: ResolvedContact | null;
  onClickPreview: (file: string) => void;
  onClickDownload: (file: string) => void;
  setUploadingFile: (value: boolean) => void;
};

type UploadTarget = 'original' | 'signed';

/**
 * A single volet (SST or PDS) card within a documentary line (AC2). Shows the
 * type badge, status, resolved signer, dates and file actions for that volet, or an
 * explicit "non concerné" state when the mission's document line has no
 * contract for this volet. Upload / view / download all live in the contextual
 * ⋮ menu (paperclip triggers are hidden).
 */
const DocumentVoletCard = ({
  volet,
  voletKind,
  missionRecordId,
  resolvedSigner,
  onClickPreview,
  onClickDownload,
  setUploadingFile,
}: DocumentVoletCardProps) => {
  const { t } = useTranslation(['legalContracts']);
  const theme = useTheme();
  const [uploadTarget, setUploadTarget] = useState<UploadTarget | null>(null);
  const typeLabel = t(`voletType.${voletKind}`);
  const typeBadgeColors =
    voletKind === TemplateContract.SST
      ? { color: theme.palette.blue[80], backgroundColor: theme.palette.blue[20] }
      : { color: theme.palette.red[60], backgroundColor: theme.palette.red[20] };
  const signerFullName = resolveSignerDisplayName(resolvedSigner);

  if (!volet) {
    return (
      <Card>
        <HeaderRow>
          <BadgesRow>
            <ContractTypeBadge {...typeBadgeColors}>{typeLabel}</ContractTypeBadge>
            <StatusChip tone="neutral">{t('badge.notApplicable')}</StatusChip>
          </BadgesRow>
        </HeaderRow>
      </Card>
    );
  }

  const { contract, badgeLabel, badgeTone, isLate } = volet;
  const sentDateLabel = contract.createdDate ? dateFormat(contract.createdDate, 'dd/MM/yyyy') : null;
  const signedDateLabel = contract.signedDate ? dateFormat(contract.signedDate, 'dd/MM/yyyy') : null;
  const canShowActions = contract.status !== 'pending';
  const fileActions = canShowActions
    ? buildFileActions(
        contract,
        onClickPreview,
        onClickDownload,
        () => setUploadTarget('original'),
        () => setUploadTarget('signed'),
        t,
      )
    : [];
  const hasSigner = Boolean(signerFullName);
  const hasDates = Boolean(sentDateLabel || signedDateLabel);

  return (
    <Card>
      <HeaderRow>
        <BadgesRow>
          <ContractTypeBadge {...typeBadgeColors}>{typeLabel}</ContractTypeBadge>
          <StatusChip tone={badgeTone}>
            <StatusIcon tone={badgeTone} />
            {badgeLabel}
          </StatusChip>
          {isLate ? (
            <LateBadge>
              <WarningAmberOutlinedIcon sx={{ fontSize: 14 }} aria-hidden />
              {t('badge.late')}
            </LateBadge>
          ) : null}
          {contract.refusedReason ? <InformationTip>{contract.refusedReason}</InformationTip> : null}
        </BadgesRow>
        {fileActions.length > 0 ? (
          <RowActionsMenu
            actions={fileActions}
            label={<MoreVertIcon sx={{ fontSize: 18, opacity: 0.65 }} aria-hidden />}
          />
        ) : null}
      </HeaderRow>

      {hasSigner || hasDates ? (
        <MetaRow>
          {hasSigner && signerFullName ? (
            <SignerSlot>
              <AvatarCell
                initials={initialsFromName(signerFullName)}
                name={signerFullName}
                tone={avatarToneFromSeed(signerFullName)}
              />
            </SignerSlot>
          ) : (
            <span />
          )}
          {hasDates ? (
            <DatesRow>
              {sentDateLabel ? (
                <span>
                  {t('sentDatePrefix')} {sentDateLabel}
                </span>
              ) : null}
              {sentDateLabel && signedDateLabel ? <DateSeparator aria-hidden>·</DateSeparator> : null}
              {signedDateLabel ? (
                <SignedDate>
                  {t('signedDatePrefix')} {signedDateLabel}
                </SignedDate>
              ) : null}
            </DatesRow>
          ) : null}
        </MetaRow>
      ) : null}

      {canShowActions ? (
        <HiddenUploadHosts>
          {contract.emptyFileUrl ? null : (
            <LegalContractFile
              legalContract={contract}
              signed={false}
              missionRecordId={missionRecordId}
              resolvedSigner={resolvedSigner}
              setUploadingFile={setUploadingFile}
              onClickPreview={onClickPreview}
              hideTrigger
              open={uploadTarget === 'original'}
              onOpenChange={(isOpen) => {
                if (!isOpen) setUploadTarget(null);
              }}
            />
          )}
          {contract.signedFileUrl ? null : (
            <LegalContractFile
              legalContract={contract}
              signed={true}
              missionRecordId={missionRecordId}
              resolvedSigner={resolvedSigner}
              setUploadingFile={setUploadingFile}
              onClickPreview={onClickPreview}
              hideTrigger
              open={uploadTarget === 'signed'}
              onOpenChange={(isOpen) => {
                if (!isOpen) setUploadTarget(null);
              }}
            />
          )}
        </HiddenUploadHosts>
      ) : null}
    </Card>
  );
};

const StatusIcon = ({ tone }: { tone: DataGridV2Tone }) => {
  const sx = { fontSize: 14 } as const;
  switch (tone) {
    case 'success':
      return <CheckCircleOutlineIcon sx={sx} />;
    case 'warning':
      return <ScheduleOutlinedIcon sx={sx} />;
    case 'danger':
      return <ErrorOutlineIcon sx={sx} />;
    case 'purple':
      return <WarningAmberOutlinedIcon sx={sx} />;
    case 'neutral':
      return <BlockOutlinedIcon sx={sx} />;
    default:
      return null;
  }
};

const buildFileActions = (
  contract: DocumentVolet['contract'],
  onClickPreview: (file: string) => void,
  onClickDownload: (file: string) => void,
  onUploadOriginal: () => void,
  onUploadSigned: () => void,
  t: (key: string) => string,
): DataGridV2RowAction[] => {
  const actions: DataGridV2RowAction[] = [];

  if (contract.emptyFileUrl) {
    actions.push({
      label: t('viewOriginal'),
      icon: <VisibilityOutlinedIcon fontSize="small" />,
      onClick: () => onClickPreview(contract.emptyFileUrl as string),
    });
  } else {
    actions.push({
      label: t('uploadOriginal'),
      icon: <UploadFileOutlinedIcon fontSize="small" />,
      onClick: onUploadOriginal,
    });
  }

  if (contract.signedFileUrl) {
    actions.push({
      label: t('downloadSigned'),
      icon: <DownloadOutlinedIcon fontSize="small" />,
      divider: true,
      onClick: () => onClickDownload(contract.signedFileUrl as string),
    });
  } else {
    actions.push({
      label: t('uploadSigned'),
      icon: <UploadFileOutlinedIcon fontSize="small" />,
      divider: true,
      onClick: onUploadSigned,
    });
  }

  // Once signed, the Yousign link is obsolete — keep download / view original only.
  if (contract.signLink && !isContractSigned(contract)) {
    actions.push({
      label: t('openSignDocument'),
      icon: <OpenInNewOutlinedIcon fontSize="small" />,
      divider: true,
      onClick: () => {
        window.open(contract.signLink as string, '_blank', 'noopener,noreferrer');
      },
    });
  }

  return actions;
};

export default DocumentVoletCard;

const Card = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  minWidth: 0,
  width: '100%',
  alignSelf: 'stretch',
  height: '100%',
  padding: '10px 12px',
  borderRadius: 12,
  border: `1px solid ${theme.palette.neutral[20]}`,
  backgroundColor: theme.palette.neutral[10],
  boxSizing: 'border-box',
}));

const HeaderRow = styled('div')(() => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 8,
  minWidth: 0,
}));

const BadgesRow = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 6,
  minWidth: 0,
}));

/** Même rouge que la pastille « X à signer » du header mission (`MissionGroupHeader`). */
const LateBadge = styled('span')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '2px 8px',
  borderRadius: 999,
  backgroundColor: '#FCE8E6',
  color: '#C0362C',
  fontSize: 12,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  lineHeight: 1.4,
}));

const MetaRow = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  minWidth: 0,
  marginTop: 'auto',
}));

const SignerSlot = styled('div')(() => ({
  minWidth: 0,
  flex: '1 1 auto',
  overflow: 'hidden',
}));

const DatesRow = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  flexShrink: 0,
  marginLeft: 'auto',
  fontSize: 11,
  color: theme.palette.neutral[60],
  whiteSpace: 'nowrap',
}));

const DateSeparator = styled('span')(({ theme }) => ({
  color: theme.palette.neutral[40],
}));

const SignedDate = styled('span')(({ theme }) => ({
  color: theme.palette.green[100],
  fontWeight: 600,
}));

/** Keeps upload modals mounted without occupying layout space. */
const HiddenUploadHosts = styled('div')(() => ({
  display: 'contents',
}));
